'use client';

import { useQuery } from '@zg-rentals/react-query5';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import {
  APARTMENT_REVIEWS_STALE_TIME_MS,
  fetchBuildingReviews,
  type BuildingReviewsSnapshot,
} from '@/src/lib/api/endpoints/apartmentReviews';

export const apartmentReviewsKeys = {
  all: ['apartmentReviews'] as const,
  building: (lotId: string) => ['apartmentReviews', 'building', lotId] as const,
};

export function useApartmentReviewsState(
  listing: PropertyDetailListing,
  snapshot?: BuildingReviewsSnapshot,
) {
  // Eligibility is lot-wide on the backend; a unit's property type can hide an eligible community.
  const enabled = listing.active !== false;
  const lotId = listing.maloneLotIdEncoded ?? '';
  const seed = snapshot?.lotId === lotId ? snapshot : undefined;
  const query = useQuery({
    queryKey: apartmentReviewsKeys.building(lotId),
    queryFn: ({ signal }) => fetchBuildingReviews(lotId, signal),
    enabled: enabled && !!lotId,
    initialData: seed?.reviews,
    initialDataUpdatedAt: seed?.updatedAt,
    staleTime: APARTMENT_REVIEWS_STALE_TIME_MS,
    retry: false,
    meta: { tags: ['apartmentReviews'], context: { api: 'hotpads-api', details: 'building reviews' } },
  });
  return { reviews: enabled ? query.data ?? null : null, isLoading: enabled && !!lotId && query.isLoading };
}

